// ============================================================================
// The pipeline board — three stages, Lead → Meet → Ready (TCSD-0207).
// 2026-07-02; cut to three 2026-09-11; since item #683 (2026-10-03) it reads the
// SUB's own status (one record for life, TCSD-0368) and labels each column from
// the one vocabulary file (TCSD-0373 ¶9): Lead · Interviewing · Bench.
//
// ⛔ The columns are PIPELINE_STAGES and nothing else; the CHECK on
// service_providers.provider_status is what enforces them.
//
// DnD: @dnd-kit/core, mirroring hr-hiring/hiring-kanban.tsx. Pointer sensor
// with an 8px activation distance so taps still open the detail sheet.
// Drag → onStageChange (optimistic in the caller, audited by the DB trigger).
// ============================================================================

import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { PIPELINE_STAGES, SUB_STATUS_LABELS, type PipelineStage } from '../subcontractors/stages';

export interface PipelineCardRow {
  id: string;
  name: string;
  stage: PipelineStage;
  /** One line under the name. */
  sub: string;
}

interface Props {
  rows: PipelineCardRow[];
  onSelect: (row: PipelineCardRow) => void;
  onStageChange: (row: PipelineCardRow, next: PipelineStage) => Promise<void> | void;
}

export function SourcingKanban({ rows, onSelect, onStageChange }: Props) {
  const [activeDrag, setActiveDrag] = useState<PipelineCardRow | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const columns = useMemo(
    () =>
      PIPELINE_STAGES.map((stage) => ({
        stage,
        label: SUB_STATUS_LABELS[stage],
        rows: rows.filter((r) => r.stage === stage),
      })),
    [rows],
  );

  function handleDragStart(e: DragStartEvent) {
    setActiveDrag(rows.find((r) => r.id === String(e.active.id)) ?? null);
  }

  async function handleDragEnd(e: DragEndEvent) {
    setActiveDrag(null);
    if (!e.over) return;
    const next = String(e.over.id) as PipelineStage;
    if (!PIPELINE_STAGES.includes(next)) return;
    const row = rows.find((r) => r.id === String(e.active.id));
    if (!row || row.stage === next) return;
    await onStageChange(row, next);
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={(e) => void handleDragEnd(e)}>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {columns.map((c) => (
          <Column key={c.stage} stage={c.stage} label={c.label} rows={c.rows} onSelect={onSelect} />
        ))}
      </div>
      <DragOverlay>
        {activeDrag ? (
          <div className="w-64 rotate-1">
            <Card row={activeDrag} onClick={() => {}} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function Column({
  stage,
  label,
  rows,
  onSelect,
}: {
  stage: PipelineStage;
  label: string;
  rows: PipelineCardRow[];
  onSelect: (row: PipelineCardRow) => void;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  return (
    <section
      ref={setNodeRef}
      data-column={stage}
      className={`w-64 shrink-0 rounded-lg border p-2 transition ${isOver ? 'border-tcs-text bg-tcs-bg-muted' : 'border-tcs-border'}`}
    >
      <h2 className="mb-2 flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
        {label}
        <span className="text-tcs-text-muted">{rows.length}</span>
      </h2>
      <ul className="space-y-2">
        {rows.map((row) => (
          <DraggableCard key={row.id} row={row} onSelect={onSelect} />
        ))}
        {rows.length === 0 && (
          <li className="rounded-md border border-dashed border-tcs-border p-3 text-center text-xs text-tcs-text-muted">—</li>
        )}
      </ul>
    </section>
  );
}

function DraggableCard({ row, onSelect }: { row: PipelineCardRow; onSelect: (row: PipelineCardRow) => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: row.id });
  return (
    <li
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={transform ? { transform: `translate(${transform.x}px, ${transform.y}px)` } : undefined}
      className={isDragging ? 'opacity-40' : undefined}
    >
      <Card row={row} onClick={() => onSelect(row)} />
    </li>
  );
}

function Card({ row, onClick }: { row: PipelineCardRow; onClick: () => void }) {
  return (
    <button
      type="button"
      data-card={row.id}
      onClick={onClick}
      className="block w-full rounded-md border border-tcs-border bg-tcs-raised p-2.5 text-left hover:border-tcs-text-muted"
    >
      <div className="truncate text-sm font-semibold text-tcs-text">{row.name}</div>
      <div className="truncate text-xs text-tcs-text-muted">{row.sub}</div>
    </button>
  );
}
